import React, { useState } from 'react';
import { X, Info, Code } from 'lucide-react';
import { useCreateEmailTemplate, availableMergeFields, extractMergeFields, type TemplateCategory } from '../../../services/queries/emailTemplateQueries';
import { StandardButton, StandardInput, StandardSelect, StandardTextarea, StandardCheckbox } from '../../standard';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'custom' as TemplateCategory,
    description: '',
    isActive: true
  });

  const [showMergeFields, setShowMergeFields] = useState(false);

  const createTemplateMutation = useCreateEmailTemplate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Extract merge fields from subject and body
      const subjectFields = extractMergeFields(formData.subject);
      const bodyFields = extractMergeFields(formData.body);
      const allMergeFields = Array.from(new Set([...subjectFields, ...bodyFields]));

      await createTemplateMutation.mutateAsync({
        ...formData,
        mergeFields: allMergeFields,
        isSystem: false
      });

      onClose();
      resetForm();
    } catch (error) {
      console.error('Failed to create template:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      body: '',
      category: 'custom',
      description: '',
      isActive: true
    });
  };

  const insertMergeField = (field: string) => {
    // Insert at cursor position in body
    const textarea = document.getElementById('template-body') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.body;
      const before = text.substring(0, start);
      const after = text.substring(end);

      setFormData(prev => ({
        ...prev,
        body: before + field + after
      }));

      // Set cursor position after inserted field
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + field.length, start + field.length);
      }, 0);
    }
  };

  if (!isOpen) return null;

  // Group merge fields by category
  const mergeFieldsByCategory = availableMergeFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, typeof availableMergeFields>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create Email Template</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Create a new email template with merge fields
            </p>
          </div>
          <button
            onClick={() => {
              onClose();
              resetForm();
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <StandardInput
                    label="Template Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Welcome Email"
                    required
                  />

                  <StandardSelect
                    label="Category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as TemplateCategory }))}
                    required
                    options={[
                      { value: 'user', label: 'User' },
                      { value: 'client', label: 'Client' },
                      { value: 'case', label: 'Case' },
                      { value: 'notification', label: 'Notification' },
                      { value: 'custom', label: 'Custom' }
                    ]}
                  />
                </div>

                <StandardInput
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of when to use this template"
                />
              </div>
            </div>

            {/* Email Content */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Email Content</h3>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-semibold mb-1">Using Merge Fields:</p>
                    <p>Insert dynamic data using merge fields like <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{"{{user.firstName}}"}</code>. Click the "Show Merge Fields" button to see available fields.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <StandardInput
                  label="Subject Line"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="e.g., Welcome to {{system.companyName}}, {{user.firstName}}!"
                  required
                  className="font-mono text-sm"
                />

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email Body <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowMergeFields(!showMergeFields)}
                      className="px-3 py-1 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <Code className="w-4 h-4" />
                      {showMergeFields ? 'Hide' : 'Show'} Merge Fields
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-4" style={{ gridTemplateColumns: showMergeFields ? '1fr 300px' : '1fr' }}>
                    <textarea
                      id="template-body"
                      value={formData.body}
                      onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                      placeholder="Write your email content here... You can use HTML tags like <h1>, <p>, <strong>, etc."
                      rows={12}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                    />

                    {showMergeFields && (
                      <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 p-3 overflow-y-auto max-h-[400px]">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                          Available Merge Fields
                        </h4>
                        {Object.entries(mergeFieldsByCategory).map(([category, fields]) => (
                          <div key={category} className="mb-4">
                            <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
                              {category}
                            </h5>
                            <div className="space-y-1">
                              {fields.map(field => (
                                <button
                                  key={field.id}
                                  type="button"
                                  onClick={() => insertMergeField(field.value)}
                                  className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors"
                                  title={`Insert ${field.label}`}
                                >
                                  <div className="font-medium text-gray-900 dark:text-white">
                                    {field.label}
                                  </div>
                                  <div className="text-gray-500 dark:text-gray-400 font-mono">
                                    {field.value}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Supports HTML formatting. Basic tags: &lt;h1&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a&gt;
                  </p>
                </div>
              </div>
            </div>

            {/* Status */}
            <StandardCheckbox
              label="Active - Template can be used immediately"
              checked={formData.isActive}
              onChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900">
          <StandardButton
            type="button"
            variant="ghost"
            onClick={() => {
              onClose();
              resetForm();
            }}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleSubmit}
            disabled={!formData.name || !formData.subject || !formData.body || createTemplateMutation.isPending}
            isLoading={createTemplateMutation.isPending}
          >
            {createTemplateMutation.isPending ? 'Creating...' : 'Create Template'}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
